/**
 * Mutation hook for the anonymous public accept endpoint (`/public/offers/:token/accept`).
 * Goes through `callPublic`, so it never carries a session.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { publicOfferLink } from '@ooh/contracts';
import type { UnitDecisionEntry } from '@ooh/contracts';
import { apiQueryKey } from '../../data/use-api-suspense-query';
import { useApiClient } from '../../plugs/api-client';

export interface PublicAcceptVersionVars {
  /** Per-unit decisions submitted once, at whole-offer finalize time (client-offer-decision-persistence). */
  unitDecisions?: UnitDecisionEntry[];
}

export function usePublicAcceptVersion(token: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: PublicAcceptVersionVars = {}) =>
      api.callPublic(publicOfferLink.publicAccept, { token, unitDecisions: vars.unitDecisions }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: apiQueryKey(publicOfferLink.publicClientView, { token }),
      });
    },
    // Non-200 errors handled at the caller via a call-level onError (409 re-enables
    // button with message). A hook-level onError still must be declared — even a
    // no-op — so MutationCache's global onError (query-client.ts) sees
    // mutation.options.onError is set and skips its own generic toast; otherwise the
    // call-level handler's toast stacks with the global one.
    onError: () => {},
  });
}
